import { useState, useMemo, useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import clsx from 'clsx';
import { useQueryClient } from '@tanstack/react-query';

import { toaster } from '~/shared/utils/toaster';
import StorageIcon from '~/shared/assets/icons/storage.svg';
import CurrentUtilizationIcon from '~/shared/assets/icons/current-utilization.svg';
import ImpactedCapacityIcon from '~/shared/assets/icons/impacted-capacity.svg';
import StorageIconInactive from '~/shared/assets/icons/total-volumes-inactive.svg';
import ImpactedCapacityIconInactive from '~/shared/assets/icons/impacted-storage-inactive.svg';
import CurrentUtilizationIconInactive from '~/shared/assets/icons/utilization-inactive.svg';
import Typography from '~/shared/components/Typography';
import Button from '~/shared/components/ButtonsV2';
import SidePanel from '~/shared/components/SidePanel';
import SearchBar from '~/shared/components/SearchBar';
import CloseIcon from '~/shared/assets/icons/arrow-right.svg';
import EditIcon from '~/shared/assets/icons/edit.svg';
import DeleteIcon from '~/shared/assets/icons/delete.svg';
import Tabs, { Tab } from '~/shared/components/Tabs';
import { useAuthContext } from '~/shared/context/useAuthContext';
import { isOrganizationView } from '~/shared/utils/accountUtils';
import UserAvatar from '~/app/components/UserAvatar';
import { usePermission } from '~/shared/hooks/usePermission';

import RuleCondition from './RuleCondition';
import MatchingVolumes from './MatchingVolumes';
import PotentialVolumes from './PotentialVolumes';
import Exceptions from './Exceptions';
import CreateExceptionModal from './CreateExceptionModal';
import RemoveExceptionModal from './RemoveExceptionModal';
import DeleteRuleModal from './DeleteRuleModal';
import ToggleRuleModal from './ToggleRuleModal';
import OptimisticToggleSwitch from './OptimisticToggleSwitch';
import { useToggleRule } from './hooks/useToggleRule';
import { useAutoscaleRuleDetails } from './hooks/useAutoscaleRuleDetails';
import { applyCreateException, applyRemoveException } from './hooks/optimisticExceptionUpdate';

import type { AutoscaleRule, AutoscaleRulesResponse } from '../hooks/useAutoscaleRulesQuery';

interface AutoscaleRuleDetailsSidePanelProps {
  rule: AutoscaleRule | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (ruleId: string, rawRule: Record<string, unknown>) => void;
}

interface KPICardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  text: string;
  isLoading?: boolean;
}

type TabValue = 'ruleCondition' | 'matchingVolumes' | 'potentialVolumes' | 'exceptions';

function formatCapacity(bytes: number): string {
  if (!bytes || bytes === 0) {
    return '0 GiB';
  }

  const k = 1024;
  const sizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB'];
  const i = Math.floor(Math.log(Math.abs(bytes)) / Math.log(k));
  const unitIndex = Math.min(i, sizes.length - 1);
  const convertedValue = bytes / Math.pow(k, unitIndex);

  return `${convertedValue.toFixed(1)} ${sizes[unitIndex]}`;
}

function SkeletonLoader({ width, className }: { width: number; className?: string }) {
  return (
    <Skeleton
      containerClassName="block leading-[unset!important]"
      className={clsx('h-[24px] 2xl:h-[24px] leading-[unset!important] rounded-[8px]', className)}
      baseColor="var(--color-gray-200)"
      highlightColor="#FFFF"
      width={width}
    />
  );
}

function KPICard({ icon: Icon, title, text, isLoading = false }: KPICardProps) {
  return (
    <div className="flex items-center p-[16px] 2xl:px-5 2xl:py-[24px]  gap-2 w-full  shadow-card rounded-[12px] border border-primary-100">
      <div className="size-9">{Icon && <Icon className="size-9!" />}</div>
      <div className="flex flex-col gap-[3px]">
        <Typography variant="p3-reg" className="" fontWeight={500}>
          {title}{' '}
        </Typography>
        <div className="flex gap-2">
          {isLoading ? (
            <SkeletonLoader width={40} />
          ) : (
            <Typography variant="H3-resp" fontWeight={500} color="text-[#2D2D32]">
              {text}
            </Typography>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AutoscaleRuleDetailsSidePanel({
  rule,
  isOpen,
  onClose,
  onEdit,
}: AutoscaleRuleDetailsSidePanelProps) {
  const { user, accounts, activeTenantId } = useAuthContext();
  const queryClient = useQueryClient();
  const canWrite = usePermission('datafy.write.autoscaling.rules');
  const isOrgView = isOrganizationView(activeTenantId, accounts || []);
  const [activeTab, setActiveTab] = useState<TabValue>('ruleCondition');
  const [isExceptionModalOpen, setIsExceptionModalOpen] = useState(false);
  const [isRemoveExceptionModalOpen, setIsRemoveExceptionModalOpen] = useState(false);
  const [selectedVolumeId, setSelectedVolumeId] = useState<string | null>(null);
  const [selectedVolumeName, setSelectedVolumeName] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedVolumeType, setSelectedVolumeType] = useState<'supported' | 'notSupported'>(
    'supported'
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [isToggleModalOpen, setIsToggleModalOpen] = useState(false);
  const [modalIntentTurningOn, setModalIntentTurningOn] = useState<boolean>(false);

  // Table-first, refetch-on-update. The list query already populates `rule.volumeData`
  // via `dry_run=true`, so we render straight from that on open. After the first
  // mutation on this rule (toggle / exception add/remove) we open the gate, and
  // `useAutoscaleRuleDetails` becomes the live source of truth.
  const [hasUpdatedSinceOpen, setHasUpdatedSinceOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setHasUpdatedSinceOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    setHasUpdatedSinceOpen(false);
  }, [rule?.ruleId]);

  const { data: ruleDetails, isFetching: isRuleDetailsFetching } = useAutoscaleRuleDetails(
    rule?.ruleId ?? null,
    isOpen && !!rule && hasUpdatedSinceOpen,
    rule?.accountId
  );

  const volumeData = ruleDetails?.volumeData ?? rule?.volumeData ?? null;

  // Only show skeletons on a genuine first load (no data to render yet). Once a rule
  // is open we always have `rule.volumeData` to fall back on, so background refetches
  // keep the existing content visible and update it in place.
  const isInitialLoading = isRuleDetailsFetching && !volumeData;

  const calculatedStats = {
    totalVolumes: ruleDetails?.volumes ?? rule?.volumes ?? 0,
    totalCapacity: ruleDetails?.capacity ?? rule?.capacity ?? 0,
    averageUtilization: ruleDetails?.utilization ?? rule?.utilization ?? '0%',
  };

  const handleSuccessWithRefresh = async ({
    message,
    title,
    volumeId,
    operation,
  }: {
    message: string;
    title: string;
    volumeId: string;
    operation: 'create' | 'remove';
  }) => {
    if (!rule?.ruleId) {
      return;
    }

    const applyPatch = operation === 'create' ? applyCreateException : applyRemoveException;

    // Open the gate so the single-rule query becomes the live source of truth and
    // refetches in the background on this and future updates.
    setHasUpdatedSinceOpen(true);

    // Patch the side-panel cache in place so the moved volume and the counts/KPIs
    // update instantly. Seed from the `rule` prop when the details query hasn't run yet.
    const detailsKey = ['autoscaleRuleDetails', rule.ruleId, rule.accountId ?? null];
    queryClient.setQueryData<AutoscaleRule | null>(detailsKey, (old) =>
      applyPatch(old ?? rule, volumeId)
    );

    // Keep the table caches consistent with the same patch.
    queryClient.setQueriesData<AutoscaleRulesResponse>({ queryKey: ['autoscaleRules'] }, (old) => {
      if (!old?.rules) {
        return old;
      }
      return {
        ...old,
        rules: old.rules.map((r) => (r.ruleId === rule.ruleId ? applyPatch(r, volumeId) : r)),
      };
    });

    toaster({
      variant: 'success',
      title: title,
      message: message,
    });

    // Silently reconcile with the server. Previous (patched) data stays visible.
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ['autoscaleRuleDetails', rule.ruleId],
        exact: false,
      }),
      queryClient.invalidateQueries({
        queryKey: ['autoscaleRules'],
        exact: false,
      }),
    ]);
  };

  const handleCreateException = (volumeId: string) => {
    setSelectedVolumeId(volumeId);
    setIsExceptionModalOpen(true);
  };

  const handleCloseExceptionModal = () => {
    setIsExceptionModalOpen(false);
    setSelectedVolumeId(null);
  };

  const handleRemoveException = (volumeId: string, volumeName?: string) => {
    setSelectedVolumeId(volumeId);
    setSelectedVolumeName(volumeName || null);
    setIsRemoveExceptionModalOpen(true);
  };

  const handleCloseRemoveExceptionModal = () => {
    setIsRemoveExceptionModalOpen(false);
    setSelectedVolumeId(null);
    setSelectedVolumeName(null);
  };

  const handleOpenDeleteModal = () => {
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDeleteSuccess = () => {
    onClose();
  };

  const matchingVolumesCount = volumeData?.datafiable.length ?? 0;
  const potentialVolumesCount = volumeData?.potentiallyDatafiable.length ?? 0;
  const exceptionsCount = volumeData?.excluded.length ?? 0;

  const tabs: Tab<TabValue>[] = useMemo(
    () => [
      {
        label: 'Rule Condition',
        value: 'ruleCondition',
      },
      {
        label: 'Matching Volumes',
        value: 'matchingVolumes',
        right: isInitialLoading ? (
          <Skeleton
            width={30}
            height={29}
            baseColor="var(--color-gray-200)"
            highlightColor="#fff"
          />
        ) : (
          <p className="bg-bg px-1.5 min-w-7.5 py-1 text-xs text-gray-700 rounded-[4px] font-family-inter font-[700]">
            {matchingVolumesCount}
          </p>
        ),
      },
      {
        label: 'Potential Volumes',
        value: 'potentialVolumes',
        right: isInitialLoading ? (
          <Skeleton
            width={30}
            height={29}
            baseColor="var(--color-gray-200)"
            highlightColor="#fff"
          />
        ) : (
          <p className="bg-bg px-1.5 min-w-7.5 py-1 text-xs text-gray-700 rounded-[4px] font-family-inter font-[700]">
            {potentialVolumesCount}
          </p>
        ),
      },
      {
        label: 'Exceptions',
        value: 'exceptions',
        right: isInitialLoading ? (
          <Skeleton
            width={30}
            height={29}
            baseColor="var(--color-gray-200)"
            highlightColor="#fff"
          />
        ) : (
          <div className="flex gap-1">
            <p className="bg-bg px-1.5 min-w-7.5 py-1 text-xs text-gray-700 rounded-[4px] font-family-inter font-[700]">
              {exceptionsCount}
            </p>
          </div>
        ),
      },
    ],
    [matchingVolumesCount, potentialVolumesCount, exceptionsCount, isInitialLoading]
  );

  const { toggleRule, isMutating } = useToggleRule({
    onSuccess: () => {
      setIsToggleModalOpen(false);
      // Open the gate so the invalidation from `useToggleRule.onSettled` triggers
      // a fresh GET /rules/{id}?dry_run=true here.
      setHasUpdatedSinceOpen(true);
    },
    onError: () => {
      setIsToggleModalOpen(false);
    },
  });

  const handleSwitchClick = (newActiveState: boolean) => {
    if (!rule) {
      return;
    }
    if (newActiveState) {
      toggleRule({ rule, newActiveState: true });
    } else {
      // If turning off, show confirmation modal
      setModalIntentTurningOn(newActiveState);
      setIsToggleModalOpen(true);
    }
  };

  const handleToggleConfirm = () => {
    if (!rule) {
      return;
    }
    toggleRule({ rule, newActiveState: modalIntentTurningOn });
  };

  const handleCloseToggleModal = () => {
    setIsToggleModalOpen(false);
  };

  const handleEdit = () => {
    if (rule && onEdit) {
      onEdit(rule.ruleId, rule.rawRule);
      onClose();
    }
  };

  function Header({ rule, isOrgView }: { rule: AutoscaleRule; isOrgView?: boolean }) {
    // The source of truth for the enabled state is the react-query cache.
    // We prioritize the `ruleDetails` from `useAutoscaleRuleDetails` if available,
    // otherwise we fall back to the `rule` prop passed from the table.
    const isEnabled = ruleDetails?.enabled ?? rule.enabled;

    const targetAccount = accounts?.find((a) => a.accountId === rule.accountId);
    const displayAccountName = targetAccount?.accountName || rule.accountId;

    return (
      <>
        <div className="flex items-center justify-between ">
          <OptimisticToggleSwitch
            switchState={isEnabled}
            onSwitchClick={handleSwitchClick}
            isPending={isMutating}
            canWrite={canWrite}
          />
          <div className="flex items-center gap-2">
            <Button className="w-10 " variant="third" icon={<EditIcon />} onClick={handleEdit} />
            <Button
              className="w-10 hover:!border-red group"
              variant="third"
              icon={
                <DeleteIcon className="size-5 group-hover:text-red transition-colors duration-300" />
              }
              onClick={handleOpenDeleteModal}
            />
            <Button
              className="w-10"
              variant="third"
              icon={<CloseIcon className="size-5" />}
              onClick={onClose}
            />
          </div>
        </div>

        <div className="py-2.5 flex flex-col gap-[18px] font-family-outfit">
          <div className="flex flex-col gap-[18px] font-family-outfit">
            <Typography variant="H3-med" className="text-2xl 2xl:text-[28px] font-[500] ">
              {rule.ruleId}
            </Typography>
          </div>

          {/* Render Meta Row ONLY in Org View */}
          {isOrgView && (
            <div className="flex divide-x-2 divide-gray-400 items-center">
              {/* Account Name */}
              <div className="pr-2">
                <Typography variant="p2-med" fontWeight={400} color="text-gray-700">
                  {displayAccountName}
                </Typography>
              </div>

              {/* User Info (Fallback to current logged in user since API lacks creator data) */}
              <div className="flex items-center gap-2 pl-2">
                <UserAvatar username={user?.name || 'Unknown User'} size="sm" />
                <Typography variant="p2-med" fontWeight={400} color="text-gray-700">
                  {user?.name}
                </Typography>
              </div>
            </div>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <SidePanel
        open={isOpen}
        onOpenChange={(open) => !open && onClose()}
        width="w-[790px]"
        excludeSelectors={[
          '.ag-row',
          '.ag-cell',
          '.auto-scale-rule-dialog',
          '.auto-scale-rule-dialog-backdrop',
          '[role="dialog"]',
          '[role="menu"]',
        ]}
      >
        {!rule ? (
          <div className="flex flex-col h-full items-center justify-center">
            <Typography variant="p3-reg" color="text-gray-500">
              No rule selected
            </Typography>
          </div>
        ) : (
          <div className="flex flex-col h-full p-6">
            <Header rule={rule} isOrgView={isOrgView} />

            <div className="py-3 flex gap-2">
              <KPICard
                icon={(ruleDetails?.enabled ?? rule.enabled) ? StorageIcon : StorageIconInactive}
                title={`Total volume${calculatedStats?.totalVolumes === 1 ? '' : 's'}`}
                text={`${calculatedStats.totalVolumes}`}
                isLoading={isInitialLoading}
              />
              <KPICard
                icon={
                  (ruleDetails?.enabled ?? rule.enabled)
                    ? ImpactedCapacityIcon
                    : ImpactedCapacityIconInactive
                }
                title="Impacted capacity"
                text={formatCapacity(calculatedStats.totalCapacity)}
                isLoading={isInitialLoading}
              />
              <KPICard
                icon={
                  (ruleDetails?.enabled ?? rule.enabled)
                    ? CurrentUtilizationIcon
                    : CurrentUtilizationIconInactive
                }
                title="Utilization"
                text={calculatedStats.averageUtilization}
                isLoading={isInitialLoading}
              />
            </div>

            <div className="mb4">
              <Tabs
                tabClassName="group"
                tabs={tabs}
                activeTab={activeTab}
                onChange={setActiveTab}
              />
            </div>

            {/* Search - Only show for volume tabs */}
            {(activeTab === 'matchingVolumes' ||
              activeTab === 'potentialVolumes' ||
              activeTab === 'exceptions') && (
              <div className=" py-5 items-center mb5">
                <SearchBar
                  size="md"
                  className="flex-1 w-full"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            )}

            {activeTab === 'ruleCondition' && <RuleCondition conditions={rule.conditions} />}
            {activeTab === 'matchingVolumes' && (
              <MatchingVolumes
                volumes={volumeData?.datafiable ?? []}
                onCreateException={handleCreateException}
                selectedVolumeType={selectedVolumeType}
                onVolumeTypeChange={setSelectedVolumeType}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                isLoading={isInitialLoading}
              />
            )}
            {activeTab === 'potentialVolumes' && (
              <PotentialVolumes
                volumes={volumeData?.potentiallyDatafiable ?? []}
                onCreateException={handleCreateException}
                selectedVolumeType={selectedVolumeType}
                onVolumeTypeChange={setSelectedVolumeType}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                isLoading={isInitialLoading}
              />
            )}
            {activeTab === 'exceptions' && (
              <Exceptions
                volumes={volumeData?.excluded ?? []}
                onCreateException={handleCreateException}
                onRemoveException={handleRemoveException}
                selectedVolumeType={selectedVolumeType}
                onVolumeTypeChange={setSelectedVolumeType}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                isLoading={isInitialLoading}
              />
            )}
          </div>
        )}
      </SidePanel>

      <CreateExceptionModal
        isOpen={isExceptionModalOpen && !!rule && !!selectedVolumeId}
        onClose={handleCloseExceptionModal}
        volumeId={selectedVolumeId || ''}
        accountId={rule?.accountId}
        onSuccess={async () => {
          const volumeId = selectedVolumeId;
          if (!volumeId) {
            return;
          }
          await handleSuccessWithRefresh({
            message: `Exception for ${volumeId} has been created. This volume will no longer be autoscaled.`,
            title: 'Exception created',
            volumeId,
            operation: 'create',
          });
        }}
      />

      <RemoveExceptionModal
        isOpen={isRemoveExceptionModalOpen && !!rule && !!selectedVolumeId}
        onClose={handleCloseRemoveExceptionModal}
        volumeId={selectedVolumeId || ''}
        accountId={rule?.accountId}
        onSuccess={async () => {
          const volumeId = selectedVolumeId;
          if (!volumeId) {
            return;
          }
          await handleSuccessWithRefresh({
            message: `Exception for ${volumeId} has been cleared`,
            title: 'Rule behavior restored',
            volumeId,
            operation: 'remove',
          });
        }}
      />

      {rule && (
        <DeleteRuleModal
          isOpen={isDeleteModalOpen}
          onClose={handleCloseDeleteModal}
          rule={rule}
          onDeleteSuccess={handleDeleteSuccess}
        />
      )}

      <ToggleRuleModal
        isOpen={isToggleModalOpen}
        onClose={handleCloseToggleModal}
        onConfirm={handleToggleConfirm}
        isTurningOn={modalIntentTurningOn}
        isPending={isMutating}
      />
    </>
  );
}
